import { useState } from 'react'
import { useAuthentication } from '../hooks/useAuthentication'
import {
  OAuthButtons,
  PasswordStrengthIndicator,
  PasswordMatchIndicator
} from './'

interface ParentLoginProps {
  onLoginSuccess?: () => void
  className?: string
}

export function ParentLogin({ onLoginSuccess, className = '' }: ParentLoginProps) {
  const [isLoginMode, setIsLoginMode] = useState(true)

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')

  const {
    isLoading,
    message,
    messageType,
    passwordStrength,
    passwordsMatch,
    login,
    signup,
    loginWithOAuth,
    clearMessage,
    updatePasswordStrength,
    updatePasswordsMatch
  } = useAuthentication(onLoginSuccess)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (isLoginMode) {
      await login(email, password)
    } else {
      if (password !== confirmPassword) {
        return
      }
      await signup(email, password, firstName, lastName)

      if (messageType === 'success') {
        setFirstName('')
        setLastName('')
        setEmail('')
        setPassword('')
        setConfirmPassword('')
      }
    }
  }

  const handlePasswordChange = (newPassword: string) => {
    setPassword(newPassword)
    if (!isLoginMode) {
      updatePasswordStrength(newPassword)
      updatePasswordsMatch(newPassword, confirmPassword)
    }
  }

  const handleConfirmPasswordChange = (newConfirmPassword: string) => {
    setConfirmPassword(newConfirmPassword)
    updatePasswordsMatch(password, newConfirmPassword)
  }

  const handleModeSwitch = () => {
    setIsLoginMode(!isLoginMode)
    clearMessage()
    setEmail('')
    setPassword('')
    setFirstName('')
    setLastName('')
    setConfirmPassword('')
  }

  return (
    <div className={`${className}`} dir="rtl">
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="text-center">
            <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
              <svg
                className="w-8 h-8 text-blue-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                />
              </svg>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 font-hebrew">
              {isLoginMode ? 'כניסת הורים' : 'הרשמה חדשה'}
            </h2>
            <p className="mt-2 text-gray-600 font-hebrew">
              מערכת תורנות הורים - בית ספר אילנות
            </p>
          </div>
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
            <OAuthButtons
              onGoogleLogin={() => loginWithOAuth('google')}
              onFacebookLogin={() => loginWithOAuth('facebook')}
              onAppleLogin={() => loginWithOAuth('apple')}
              isLoading={isLoading}
              className="mb-6"
            />

            <div className="relative mb-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500 font-hebrew">או</span>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {!isLoginMode && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label
                      htmlFor="firstName"
                      className="block text-sm font-medium text-gray-700 font-hebrew mb-2"
                    >
                      שם פרטי
                    </label>
                    <input
                      id="firstName"
                      type="text"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-right"
                      placeholder="שם פרטי"
                      disabled={isLoading}
                      dir="rtl"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="lastName"
                      className="block text-sm font-medium text-gray-700 font-hebrew mb-2"
                    >
                      שם משפחה
                    </label>
                    <input
                      id="lastName"
                      type="text"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-right"
                      placeholder="שם משפחה"
                      disabled={isLoading}
                      dir="rtl"
                    />
                  </div>
                </div>
              )}

              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700 font-hebrew mb-2"
                >
                  כתובת דוא"ל
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-right"
                  placeholder="your@gmail.com"
                  disabled={isLoading}
                  dir="ltr"
                />
              </div>

              <div>
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-gray-700 font-hebrew mb-2"
                >
                  סיסמה
                </label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => handlePasswordChange(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-right"
                  placeholder={isLoginMode ? 'סיסמה' : 'לפחות 6 תווים'}
                  disabled={isLoading}
                  dir="ltr"
                />

                {!isLoginMode && (
                  <PasswordStrengthIndicator password={password} strength={passwordStrength} />
                )}
              </div>

              {!isLoginMode && (
                <div>
                  <label
                    htmlFor="confirmPassword"
                    className="block text-sm font-medium text-gray-700 font-hebrew mb-2"
                  >
                    אישור סיסמה
                  </label>
                  <input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => handleConfirmPasswordChange(e.target.value)}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-right"
                    placeholder="הקלד שוב את הסיסמה"
                    disabled={isLoading}
                    dir="ltr"
                  />

                  <PasswordMatchIndicator
                    confirmPassword={confirmPassword}
                    passwordsMatch={passwordsMatch}
                  />
                </div>
              )}

              {message && (
                <div
                  className={`p-4 rounded-lg ${
                    messageType === 'success'
                      ? 'bg-green-50 border border-green-200'
                      : 'bg-red-50 border border-red-200'
                  }`}
                >
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      {messageType === 'success' ? (
                        <svg
                          className="w-5 h-5 text-green-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                      ) : (
                        <svg
                          className="w-5 h-5 text-red-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.728-.833-2.498 0L4.316 16.5c-.77.833.192 2.5 1.732 2.5z"
                          />
                        </svg>
                      )}
                    </div>
                    <div className="mr-3">
                      <p
                        className={`text-sm font-hebrew ${
                          messageType === 'success' ? 'text-green-800' : 'text-red-800'
                        }`}
                      >
                        {message}
                      </p>
                    </div>
                    <div className="mr-auto">
                      <button
                        type="button"
                        onClick={clearMessage}
                        className={`-mx-1.5 -my-1.5 rounded-lg p-1.5 inline-flex h-8 w-8 ${
                          messageType === 'success'
                            ? 'text-green-500 hover:bg-green-100'
                            : 'text-red-500 hover:bg-red-100'
                        }`}
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div>
                <button
                  type="submit"
                  disabled={
                    isLoading ||
                    !email.trim() ||
                    !password.trim() ||
                    (!isLoginMode &&
                      (!firstName.trim() ||
                        !lastName.trim() ||
                        !confirmPassword.trim() ||
                        passwordsMatch === false))
                  }
                  className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed font-hebrew"
                >
                  {isLoading ? (
                    <>
                      <svg
                        className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      {isLoginMode ? 'מתחבר...' : 'נרשם...'}
                    </>
                  ) : (
                    <>{isLoginMode ? 'כניסה' : 'הרשמה'}</>
                  )}
                </button>
              </div>
            </form>

            <div className="mt-6 text-center">
              <button onClick={handleModeSwitch} className="text-sm text-blue-600 hover:text-blue-500 font-hebrew">
                {isLoginMode ? 'עדיין אין לך חשבון? הירשם כאן' : 'כבר יש לך חשבון? התחבר כאן'}
              </button>
            </div>

            <div className="mt-6 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="text-center">
                <h4 className="text-sm font-medium text-blue-900 font-hebrew mb-2 flex items-center justify-center gap-1">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                    />
                  </svg>
                  אבטחת המידע שלך
                </h4>
                <div className="text-xs text-blue-800 font-hebrew space-y-1">
                  <p>
                    • <strong>המידע נשמר:</strong> במסד נתונים מאובטח (Supabase)
                  </p>
                  <p>
                    • <strong>הצפנה:</strong> סיסמאות מוצפנות באופן מלא
                  </p>
                  <p>
                    • <strong>גישה:</strong> רק אתם יכולים לראות את הפרטים שלכם
                  </p>
                  <p>
                    • <strong>מחיקה:</strong> ניתן למחוק חשבון בכל עת
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="text-center">
                <h4 className="text-sm font-medium text-gray-900 font-hebrew mb-2">צריך עזרה?</h4>
                <p className="text-xs text-gray-600 font-hebrew mb-3">
                  פנה לאבי לוין נציג המיזם מטעם ההנהגה
                </p>
              </div>
            </div>
          </div>

          <div className="mt-6 text-center space-y-2">
            <p className="text-xs text-gray-500 font-hebrew">
              מערכת תורנות הורים - בית ספר יסודי אילנות תל אביב
            </p>
            <div className="text-xs text-gray-400 space-y-1">
              <p>🔒 מוגן עם Supabase - מסד נתונים ענן מאובטח</p>
              <p>🌐 הצפנה מלאה | 🇮🇱 שרתים באירופה | 📱 גיבוי אוטומטי</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
